import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Gift,
  Sparkles,
  Clock,
  Flame,
  Heart,
  MessageCircle,
  MapPin,
  Phone,
  ArrowRight,
  Check,
} from "lucide-react";
import { MESSENGER_URL } from "@/constants/content";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.12 } } };

const CARDS = [
  {
    duration: "30 perc",
    price: "5 000 Ft",
    accent: "#5BB5A9",
    icon: Sparkles,
    title: "Klasszik",
    redeem: [
      "Svéd relaxáló masszázs",
      "Hátmasszázs",
      "Talpmasszázs",
      "Arcmasszázs",
      "Kézmasszázs",
    ],
  },
  {
    duration: "30 perc",
    price: "7 500 Ft",
    accent: "#D4AF37",
    icon: Flame,
    title: "Harmónia",
    note: "A 30 perces kezelések Thermo, meleg olajos, aromaterápiás verzióban – arc és kéz kivételével. Immunerősítő masszázs is választható.",
    redeem: [
      "Svéd Thermo masszázs",
      "Hát Thermo masszázs",
      "Talp Thermo masszázs",
      "Immunerősítő masszázs",
    ],
  },
  {
    duration: "60 perc",
    price: "12 000 Ft",
    accent: "#2A9D8F",
    icon: Heart,
    title: "Feltöltődés",
    redeem: [
      "Relaxáló Svéd masszázs",
      "Stresszoldó masszázs",
      "Detox-Duó kéz- vagy arcmasszázs kiegészítővel",
      "AromaFlow + 10 perc kéz- vagy talpmasszázs",
    ],
  },
  {
    duration: "75 perc",
    price: "15 000 Ft",
    accent: "#A88A2B",
    icon: Gift,
    title: "Luxus",
    highlight: true,
    redeem: [
      "Teljes test + Arcmasszázs",
      "Teljes test + Talpmasszázs",
      "Stresszoldó teljes testmasszázs",
      "Herballabdacsos kezelés",
      "Thermóval is elérhető",
    ],
  },
];

function GiftCard({ card, index }) {
  const Icon = card.icon;
  return (
    <motion.div
      variants={fadeUp}
      data-testid={`gift-card-${index}`}
      className={`relative bg-white rounded-[2rem] border border-[#EAE5D9] shadow-[0_20px_60px_-30px_rgba(28,107,97,0.35)] overflow-hidden flex flex-col ${
        card.highlight ? "ring-1 ring-[#D4AF37]/40" : ""
      }`}
    >
      {card.highlight && (
        <span className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] bg-[#FAF5E6] border border-[#EEDEB0] px-3 py-1 rounded-full">
          Kedvencünk
        </span>
      )}

      <div
        className="h-2 w-full"
        style={{ background: `linear-gradient(90deg, ${card.accent} 0%, ${card.accent}88 100%)` }}
      />

      <div className="p-7 md:p-8 flex-1 flex flex-col">
        <div className="flex items-center gap-3">
          <div
            className="h-11 w-11 rounded-full flex items-center justify-center text-white shadow-sm"
            style={{ backgroundColor: card.accent }}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A88A2B]">
              Ajándékkártya
            </div>
            <div className="text-[13px] text-[#758C88] mt-0.5 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {card.duration}
            </div>
          </div>
        </div>

        <h3 className="font-serif-display text-2xl md:text-[26px] text-[#1A2825] mt-5 leading-snug">
          {card.title}
        </h3>

        <div className="mt-3 flex items-baseline gap-2">
          <span
            className="font-serif-display text-[34px] leading-none"
            style={{ color: card.accent }}
          >
            {card.price}
          </span>
        </div>

        {card.note && (
          <p className="mt-4 text-[13px] text-[#3A4D4A] italic font-light leading-relaxed">
            {card.note}
          </p>
        )}

        <div className="mt-5 gold-rule opacity-70" />

        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#758C88] mb-3">
            Mire váltható be
          </div>
          <ul className="space-y-2">
            {card.redeem.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-[14px] text-[#3A4D4A] font-light">
                <Check
                  className="h-4 w-4 mt-0.5 flex-shrink-0"
                  style={{ color: card.accent }}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <a
          href={MESSENGER_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`gift-card-${index}-cta`}
          className="mt-7 inline-flex items-center justify-center gap-2 text-white px-6 py-3 rounded-full font-medium tracking-wide transition-all hover:-translate-y-0.5"
          style={{
            backgroundColor: card.accent,
            boxShadow: `0 10px 24px ${card.accent}55`,
          }}
        >
          <MessageCircle className="h-4 w-4" /> Megrendelem Messengeren
        </a>
      </div>
    </motion.div>
  );
}

export default function Ajandekkartya() {
  return (
    <div data-testid="ajandekkartya-page" className="pt-4">
      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-10 md:pb-14">
        <motion.div
          initial="hidden"
          animate="show"
          variants={fadeUp}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">
            <Gift className="h-3.5 w-3.5" /> Ajándékkártya
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#1A2825] mt-5 leading-[1.05]">
            Ajándékozz <em className="not-italic text-[#2A9D8F]">valódi</em> feltöltődést
          </h1>
          <p className="mt-6 text-[#3A4D4A] font-light leading-relaxed text-[16px] md:text-[17px]">
            A TestLélekHarmónia Masszázs Stúdió ajándékkártyái állandó kínálatunk részei —
            elegáns kivitelben, időtartamra szólóan érhetők el. Mert az élmény mindig jó választás.
          </p>
        </motion.div>
      </section>

      {/* Cards */}
      <section className="relative max-w-6xl mx-auto px-6 md:px-12 pb-16 md:pb-20">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7"
        >
          {CARDS.map((c, i) => (
            <GiftCard key={c.title} card={c} index={i} />
          ))}
        </motion.div>
      </section>

      {/* Info bar */}
      <section className="relative max-w-6xl mx-auto px-6 md:px-12 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="bg-[#F6F3EB] rounded-[2rem] border border-[#EAE5D9] p-8 md:p-10 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-white border border-[#EAE5D9] flex items-center justify-center flex-shrink-0">
              <Clock className="h-4 w-4 text-[#2A9D8F]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#A88A2B]">Érvényesség</div>
              <div className="text-[#1A2825] font-medium mt-1">Vásárlástól számított 2 hónap</div>
              <div className="text-[13px] text-[#3A4D4A] font-light mt-1">
                Bőven van idő beváltani egy kényelmes időpontban.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-white border border-[#EAE5D9] flex items-center justify-center flex-shrink-0">
              <MapPin className="h-4 w-4 text-[#2A9D8F]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#A88A2B]">Vásárlás &amp; átvétel</div>
              <div className="text-[#1A2825] font-medium mt-1">TestLélekHarmónia Stúdió</div>
              <div className="text-[13px] text-[#3A4D4A] font-light mt-1">
                2316 Tököl, Csépi út 114
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-white border border-[#EAE5D9] flex items-center justify-center flex-shrink-0">
              <Phone className="h-4 w-4 text-[#2A9D8F]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#A88A2B]">Kapcsolat</div>
              <a
                href="tel:+36704324269"
                data-testid="ajandekkartya-phone-rita"
                className="block text-[#1A2825] font-medium mt-1 hover:text-[#2A9D8F] transition-colors"
              >
                Rita · 0670/432-4269
              </a>
              <a
                href="tel:+36307153708"
                data-testid="ajandekkartya-phone-anna"
                className="block text-[#1A2825] font-medium hover:text-[#2A9D8F] transition-colors"
              >
                Annamari · 0630/715-3708
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="relative max-w-4xl mx-auto px-6 md:px-12 pb-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="bg-[#1A2825] text-white rounded-[2.5rem] p-10 md:p-14 relative overflow-hidden"
        >
          <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-[80px]" />
          <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-[#2A9D8F]/15 blur-[80px]" />
          <div className="relative">
            <Sparkles className="h-6 w-6 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="font-serif-display text-3xl md:text-4xl font-light leading-tight">
              Írj Messengeren és <em className="not-italic text-[#D4AF37]">összecsomagoljuk</em> az ajándékod
            </h2>
            <p className="mt-5 text-white/75 font-light leading-relaxed max-w-xl mx-auto">
              Küldd el üzenetben, hogy melyik kártyát szeretnéd, kinek szánod és mikor tudnád átvenni —
              rögtön válaszolunk.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={MESSENGER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ajandekkartya-messenger-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#2A9D8F] hover:bg-[#22857a] text-white px-7 py-3.5 rounded-full font-medium tracking-wide transition-all shadow-[0_10px_24px_rgba(42,157,143,0.35)] hover:-translate-y-0.5"
              >
                <MessageCircle className="h-4 w-4" /> Megrendelem Messengeren
              </a>
              <Link
                to="/masszazs"
                data-testid="ajandekkartya-massage-link"
                className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 text-white px-7 py-3.5 rounded-full font-medium tracking-wide transition-all"
              >
                Kezelések áttekintése <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
